import React, { useState } from 'react';
import { Table, Card, DatePicker, Row, Col, Form, Select } from 'antd';
import dayjs from 'dayjs';
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '../../services/react-query/queryKeys';
import { useSelector } from 'react-redux';
import { getCashFlow } from '../../network/treeTable';
import Heading from '../../components/common/Heading/Heading';
import { getSearchApi } from '../../network/itemList';
import DashboardCards from '../Dashboard/DashboardCards';

const CashFlow = ({ t }) => {
  const [form] = Form.useForm();
  const { currentLang, companyName } = useSelector((state) => state.user);
  const [sourceData, setSourceData] = useState({
    report_date: dayjs().format('YYYY-MM-DD'),
    cost_center: '',
    period_start_date: dayjs().startOf('year').format('YYYY-MM-DD'),
    period_end_date: dayjs().endOf('year').format('YYYY-MM-DD'),
    from_fiscal_year: dayjs().format('YYYY'),
    to_fiscal_year: dayjs().format('YYYY'),
  });

  const { data: getTreeDataDetails, isLoading } = useQuery({
    queryKey: [
      queryKeys.getCashFlow,
      companyName,
      currentLang,
      sourceData.cost_center,
      sourceData.report_date,
      sourceData.period_start_date,
      sourceData.period_end_date,
      sourceData.from_fiscal_year,
      sourceData.to_fiscal_year,
    ],
    queryFn: () =>
      getCashFlow({
        currentLang,
        companyName,
        cost_center: sourceData.cost_center,
        report_date: sourceData.report_date,
        period_start_date: sourceData.period_start_date,
        period_end_date: sourceData.period_end_date,
        from_fiscal_year: sourceData.from_fiscal_year,
        to_fiscal_year: sourceData.to_fiscal_year,
      }),
  });
  const { data: costCenters } = useQuery({
    queryKey: [queryKeys.getCostCentersSearch],
    queryFn: () => getSearchApi('Cost Center'),
    keepPreviousData: true,
  });
  const columns = getTreeDataDetails?.message?.columns?.map((col) => ({
    title: col?.label,
    dataIndex: col?.fieldname,
    key: col?.fieldname,
  }));

  const buildAccountsTree = (accounts) => {
    const map = new Map();

    accounts?.forEach((account) => {
      map.set(account?.account, { ...account, key: account?.account });
    });

    const result = [];
    accounts?.forEach((account) => {
      if (account?.parent_account) {
        const parent = map.get(account.parent_account);
        if (parent) {
          if (!parent.children) {
            parent.children = [];
          }
          parent.children.push(map.get(account.account));
        }
      } else {
        result.push(map.get(account.account));
      }
    });
    const cleanTree = (nodes) => {
      return nodes?.map((node) => {
        const { children, ...rest } = node;
        return children && children.length > 0
          ? { ...rest, children: cleanTree(children) }
          : rest;
      });
    };

    return cleanTree(result.filter((account) => account?.key !== undefined));
  };

  const accountsTree = buildAccountsTree(
    getTreeDataDetails?.message?.result || []
  );
  // console.log(accountsTree);

  return (
    <div>
      {/* <Heading title={t.reports} subTitle={t.CashFlow} path="/" /> */}
      <DashboardCards t={t} />
      <Card className="mt-5">
        <Form name="basic" layout="vertical" form={form}>
          <Row gutter={[16, 0]}>
            <Col xs={24} md={12} xl={8} xxl={8}>
              <Form.Item
                name="period_data"
                // label={t.date}
                initialValue={[dayjs().startOf('year'), dayjs()]}
              >
                <DatePicker.RangePicker
                  format="YYYY-MM-DD"
                  style={{ width: '100%' }}
                  defaultValue={[dayjs().startOf('year'), dayjs()]}
                  onChange={(dates, dateStrings) => {
                    setSourceData({
                      ...sourceData,
                      period_start_date: dateStrings[0],
                      period_end_date: dateStrings[1],
                    });
                  }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} xl={8} xxl={8}>
              <Form.Item
                name="period_data"
                // label={t.date}
                initialValue={[dayjs(), dayjs()]}
              >
                <DatePicker.RangePicker
                  format="YYYY"
                  picker="year"
                  style={{ width: '100%' }}
                  defaultValue={[dayjs(), dayjs()]}
                  onChange={(dates, dateStrings) => {
                    setSourceData({
                      ...sourceData,
                      from_fiscal_year: dateStrings[0],
                      to_fiscal_year: dateStrings[1],
                    });
                  }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} xl={8} xxl={8}>
              <Form.Item
                name="cost_center"
                // label={t.costCenter}
              >
                <Select
                  placeholder={t.costCenter}
                  showSearch
                  allowClear
                  popupMatchSelectWidth={false}
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.label ?? '').toLocaleLowerCase().includes(input)
                  }
                  filterSort={(optionA, optionB) =>
                    (optionA?.label ?? '')
                      .toLowerCase()
                      .localeCompare((optionB?.label ?? '').toLowerCase())
                  }
                  options={costCenters?.message?.map((item) => ({
                    value: item?.value,
                    label: item?.value,
                  }))}
                  onChange={(value) => {
                    setSourceData({
                      ...sourceData,
                      cost_center: `${
                        value === undefined ? '[]' : `["${value}"]`
                      }`,
                    });
                  }}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>

        <Table
          className="table_custom"
          columns={columns}
          dataSource={accountsTree}
          loading={isLoading}
          pagination={{ hideOnSinglePage: true }}
          // rowKey="key"
          scroll={{ x: 'max-content' }}
        />
      </Card>
    </div>
  );
};

export default CashFlow;
